import { useState, useRef, useEffect } from 'react';
import { Text, TextInput, Button, Flex, Badge } from '@tremor/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useBatches } from '../../hooks/useStats';
import type { UploadFilesResponse } from '../../types';

interface FileUploadTabProps {
  onClose: () => void;
  bundleKey?: string;
  onResultChange?: (hasResult: boolean) => void;
}

export function FileUploadTab({ onClose, bundleKey, onResultChange }: FileUploadTabProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [batchName, setBatchName] = useState('');
  const [skipProcessed, setSkipProcessed] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadFilesResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { data: batchesData } = useBatches();

  useEffect(() => {
    onResultChange?.(result !== null);
  }, [result, onResultChange]);

  // While the upload tab is mounted, stop the browser from opening a file that
  // gets dropped just outside the drop zone (its default is to navigate to it).
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
      // Reset input so the same file can be re-added
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    // Required: without preventDefault the browser treats the drop as a
    // navigation and opens/downloads the file instead of letting us handle it.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Ignore leave events fired when moving over a child element.
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) {
      setFiles(prev => [...prev, ...dropped]);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one PDF, .docx, or .doc file');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.uploadFiles({
        files,
        batchName: batchName || undefined,
        skipProcessed,
        bundleKey: bundleKey || undefined,
      });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (result) {
    const batch = batchesData?.batches.find(b => b.name === result.batchName);
    const hasQueued = result.summary.queued > 0;
    const isComplete = !!batch && batch.processing === 0 && batch.pending === 0;
    const canViewDocuments = !hasQueued || isComplete;

    const handleViewDocuments = () => {
      navigate(`/documents?batch=${encodeURIComponent(result.batchName)}`);
      onClose();
    };

    return (
      <>
        <div className="mt-4 space-y-3">
          {/* Header / status banner */}
          {hasQueued ? (
            isComplete ? (
              <div className="rounded p-3 bg-emerald-950/30 border border-emerald-900/50">
                <Text className="text-emerald-400 font-medium">Processing complete</Text>
              </div>
            ) : (
              <div className="rounded p-3 bg-blue-950/30 border border-blue-900/50">
                <Text className="text-blue-300 font-medium">
                  {batch ? 'Processing…' : 'Starting…'}
                </Text>
                <Text className="text-blue-400/70 text-xs mt-0.5">
                  {batch
                    ? 'Documents are moving through extract, evaluate, and annotate.'
                    : 'Waiting for workers to pick up the batch.'}
                </Text>
              </div>
            )
          ) : (
            <div className="rounded p-3 bg-amber-950/30 border border-amber-900/50">
              <Text className="text-amber-400 font-medium">No Files Queued</Text>
            </div>
          )}

          {/* Intake summary */}
          <div className="text-sm">
            <Text className="text-gray-300 truncate" title={result.batchName}>
              Batch <span className="font-medium">{result.batchName}</span>
            </Text>
            <Text className="text-gray-500 text-xs mt-0.5">
              {result.summary.queued.toLocaleString()} queued
              {' · '}
              {result.summary.skipped.toLocaleString()} skipped
              {result.summary.failed > 0 && (
                <span className="text-red-400">
                  {' · '}
                  {result.summary.failed.toLocaleString()} failed
                </span>
              )}
            </Text>
          </div>

          {/* Live progress bar */}
          {hasQueued && batch && (
            <div className="space-y-1.5">
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full flex">
                  {batch.completed > 0 && (
                    <div
                      className="bg-emerald-500 h-full"
                      style={{ width: `${(batch.completed / batch.documentCount) * 100}%` }}
                    />
                  )}
                  {batch.skipped > 0 && (
                    <div
                      className="bg-orange-500 h-full"
                      style={{ width: `${(batch.skipped / batch.documentCount) * 100}%` }}
                    />
                  )}
                  {batch.processing > 0 && (
                    <div
                      className="bg-amber-500 h-full"
                      style={{ width: `${(batch.processing / batch.documentCount) * 100}%` }}
                    />
                  )}
                  {batch.failed > 0 && (
                    <div
                      className="bg-red-500 h-full"
                      style={{ width: `${(batch.failed / batch.documentCount) * 100}%` }}
                    />
                  )}
                </div>
              </div>
              <Flex justifyContent="between" className="text-xs">
                <Text className="text-gray-400">
                  <span className="text-emerald-400 font-medium">
                    {batch.completed.toLocaleString()}
                  </span>
                  {' / '}
                  {batch.documentCount.toLocaleString()} completed
                </Text>
                <div className="flex items-center gap-2">
                  {batch.processing > 0 && (
                    <span className="text-amber-400">{batch.processing.toLocaleString()} processing</span>
                  )}
                  {batch.failed > 0 && (
                    <span className="text-red-400">{batch.failed.toLocaleString()} failed</span>
                  )}
                  {batch.skipped > 0 && (
                    <span className="text-orange-400">{batch.skipped.toLocaleString()} skipped</span>
                  )}
                </div>
              </Flex>
            </div>
          )}

          {/* Per-file upload errors */}
          {result.errors && result.errors.length > 0 && (
            <div className="bg-red-950/30 border border-red-900/50 rounded p-2">
              <Text className="text-red-400 text-xs font-medium mb-1">Errors:</Text>
              {result.errors.map((err, i) => (
                <Text key={i} className="text-red-300 text-xs">
                  {err.fileName}: {err.error}
                </Text>
              ))}
            </div>
          )}
        </div>

        <Flex justifyContent="end" className="mt-6 gap-3">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleViewDocuments} disabled={!canViewDocuments}>
            View Documents
          </Button>
        </Flex>
      </>
    );
  }

  return (
    <>
      <div className="mt-4 space-y-4">
        {/* File picker */}
        <div>
          <Text className="text-gray-400 text-sm mb-1">PDF or Word files</Text>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-gray-700 hover:border-gray-500'
            }`}
          >
            <Text className="text-gray-400 text-sm">
              {isDragging ? 'Drop files to add them' : 'Click to select PDF, .docx, or .doc files, or drag and drop'}
            </Text>
            <Text className="text-gray-600 text-xs mt-1">
              Max 50 files, 50MB each
            </Text>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.doc"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="max-h-40 overflow-y-auto space-y-1">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-800/50 rounded px-3 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Text className="text-gray-300 text-sm truncate">{file.name}</Text>
                  <Badge size="xs" color="gray">{formatSize(file.size)}</Badge>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="text-gray-500 hover:text-red-400 text-sm ml-2 flex-shrink-0"
                >
                  &#10005;
                </button>
              </div>
            ))}
            <div className="text-xs text-gray-500 pt-1">
              {files.length} file{files.length !== 1 ? 's' : ''} ({formatSize(totalSize)})
            </div>
          </div>
        )}

        {/* Batch name */}
        <div>
          <Text className="text-gray-400 text-sm mb-1">Batch Name (optional)</Text>
          <TextInput
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            placeholder={files.length > 0 ? `File Upload (${files.length} file${files.length !== 1 ? 's' : ''})` : 'Auto-generated'}
            className="bg-gray-800 border-gray-700"
          />
        </div>

        {/* Skip processed */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="uploadSkipProcessed"
            checked={skipProcessed}
            onChange={(e) => setSkipProcessed(e.target.checked)}
            className="rounded bg-gray-800 border-gray-700"
          />
          <label htmlFor="uploadSkipProcessed" className="text-gray-400 text-sm">
            Skip already processed documents
          </label>
        </div>

        <div className="bg-blue-950/30 border border-blue-900/50 rounded p-2">
          <Text className="text-blue-300 text-xs">
            Uploaded files will be processed through extract, evaluate, and annotate. The tag job (iConect write-back) will be skipped.
          </Text>
        </div>

        {error && (
          <div className="bg-red-950/30 border border-red-900/50 rounded p-2">
            <Text className="text-red-400 text-sm">{error}</Text>
          </div>
        )}
      </div>

      <Flex justifyContent="end" className="mt-6 gap-3">
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleUpload} loading={isLoading} disabled={files.length === 0}>
          Upload {files.length > 0 ? `${files.length} File${files.length !== 1 ? 's' : ''}` : ''}
        </Button>
      </Flex>
    </>
  );
}
